type DisablableElement = HTMLFieldSetElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type InputLike = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type ListenerTarget = {
    element: InputLike;
    requiredValue?: RegExp;
    negated: boolean;
}

function parseListenerTargets( str: string, container: HTMLFormElement ): Array<ListenerTarget> {
    let targets: Array<ListenerTarget> = [];

    const split: Array<string> = str.split( ',' );

    split.forEach( ( str: string ) => {
        let negated = false;

        let parts: Array<string> = [];

        if ( str.includes( '!=' ) ) {
            parts = str.split( '!=' );
            negated = true;
        } else {
            parts = str.split( '=' );
        }

        const targetName = parts[ 0 ];

        container.querySelectorAll( `[name="${targetName}"]` ).forEach( ( targetElement: InputLike ) => {
            const target: ListenerTarget = {
                element: targetElement,
                negated: negated,
            };

            if ( parts.length === 2 ) {
                const valuePattern = new RegExp( parts[ 1 ] );
                target.requiredValue = valuePattern;
            }

            targets = [
                ...targets,
                target,
            ];
        } );
    } );

    return targets;
}

class InputGroupListener {
    _id: string;
    _element: DisablableElement;
    _targets: Array<ListenerTarget> = [];

    constructor( element: DisablableElement ) {
        this._id = getElementIdentifer( element );

        if ( !this._id ) {
            throw new Error( 'cannot construct InputGroupListener from element without a name/id attribute' );
        }

        this._element = element;
        this._element.dataset.enabledByInitialised = 'true';

        const parentForm = element.closest( 'form' );

        if ( !parentForm ) {
            throw new Error( 'cannit initialise an InputGroupListener outside a <form>' );
        }

        this._targets = parseListenerTargets( element.dataset.enabledBy as string, parentForm );
        this.listen();

        this._element.addEventListener( 'inputgroup:test', this._testAll.bind( this ) );

        document.addEventListener( 'inputgroup:disabled', this._onInputGroupDisabled.bind( this ) );
        document.addEventListener( 'inputgroup:enabled', this._onInputGroupEnabled.bind( this ) );
    }

    listen(): void {
        this._targets.forEach( ( target: ListenerTarget ) => {
            if ( target.element.nodeName.toLowerCase() === 'fieldset' ) {
                target.element.addEventListener( 'inputgroup:disabled', this.disable.bind( this ) );
                target.element.addEventListener( 'inputgroup:enabled', this._testAll.bind( this ) );
                return;
            }
            target.element.addEventListener( 'input', this._testAll.bind( this ) );
        } );
    }

    enable(): void {
        this._element.disabled = false;
        this._element.querySelectorAll( 'input,select,textarea' ).forEach( ( element: DisablableElement ) => {
            const parentInputGroup = element.closest<DisablableElement>( '[data-enabled-by]' );

            if ( parentInputGroup && getElementIdentifer( parentInputGroup ) !== this._id ) {
                return;
            }

            element.disabled = false;
        } );
        this._element.dispatchEvent( new CustomEvent( 'inputgroup:enabled', { bubbles: true } ) );
    }

    disable(): void {
        this._element.disabled = true;
        this._element.querySelectorAll( 'input,select,textarea' ).forEach( ( element: DisablableElement ) => {
            if ( element.dataset.enabledBy ) {
                return;
            }
            element.disabled = true;
        } );
        this._element.dispatchEvent( new CustomEvent( 'inputgroup:disabled', { bubbles: true } ) );
    }

    _onInputGroupDisabled( event: CustomEvent ): void {
        if ( !event.target || !( event.target instanceof HTMLElement ) ) {
            return;
        }

        const target = event.target as DisablableElement;

        if ( this._element.name === target.name ) {
            return;
        }

        const ownTargetInDisabledElement = this._targets.some( ( t ) => t.element.closest( `[name="${target.name}"]` ) !== null );

        if ( !ownTargetInDisabledElement ) {
            return;
        }

        this.disable();
    }

    _onInputGroupEnabled( event: CustomEvent ): void {
        if ( !event.target || !( event.target instanceof HTMLElement ) ) {
            return;
        }

        const target = event.target as DisablableElement;

        if ( this._element.name === target.name ) {
            return;
        }

        const ownTargetInEnabledElement = this._targets.some( ( t ) => t.element.closest( `[name="${target.name}"]` ) !== null );

        if ( !ownTargetInEnabledElement ) {
            return;
        }

        this._testAll();
    }

    _testAll( event?: Event ): void {
        for ( let i = 0; i < this._targets.length; i++ ) {
            if ( this._test( this._targets[ i ] ) ) {
                this.enable();
                return;
            }
        }
        this.disable();
    }

    _test( target: ListenerTarget ): boolean {
        const testValue = (): boolean => {
            if ( target.element.type !== 'checkbox' && !target.requiredValue ) {
                return target.element.value.length > 0;
            }

            if ( target.element.type === 'checkbox' && !target.requiredValue ) {
                return ( target.element as HTMLInputElement ).checked;
            }

            const matches: boolean = ( target.requiredValue as RegExp ).test( target.element.value );

            if ( target.element.type === 'checkbox' ) {
                if ( target.negated && !matches && ( target.element as HTMLInputElement ).checked ) {
                    return true;
                }

                if ( target.negated && matches && ( target.element as HTMLInputElement ).checked ) {
                    return false;
                }

                return matches && ( target.element as HTMLInputElement ).checked;
            }

            if ( target.negated && !matches ) {
                return true;
            }

            if ( target.negated && matches ) {
                return false;
            }

            return matches;
        };

        switch ( target.element.nodeName.toLowerCase() ) {
        case 'textarea':
        case 'select':
            return testValue();
        case 'input':
            return testValue();
        case 'fieldset':
            if ( target.element.disabled ) {
                return false;
            }
            break;
        default:
            throw new Error( `unsupported element node type "${target.element.nodeName}"` );
        }

        return false;
    }
}

function getElementIdentifer( element: DisablableElement ): string {
    if ( element.name && element.name.length > 0 ) {
        return element.name;
    }
    return '';
}

export default function initInputEnablers(): void {
    function init() {
        document.querySelectorAll<DisablableElement>( '[data-enabled-by]' ).forEach( ( element: DisablableElement ) => {
            if ( element.dataset.enabledByInitialised && element.dataset.enabledByInitialised === 'true' ) {
                return;
            }
            new InputGroupListener( element );
        } );
    }

    init();

    window.addEventListener( 'twc:newcontentloaded', init );
}
